import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { MarketingConsentFields, MARKETING_CONSENT_BONUS, type MarketingConsent } from './MarketingConsentFields';
import { MarketingBonusNudge } from './MarketingBonusNudge';

const HIDDEN_PATH_PREFIXES = ['/login', '/register', '/social-register', '/admin', '/desktop-auth-complete', '/share', '/marketing-consent'];

/** Consent decisions and 7-day deferrals are shared across web and desktop. */
export function MarketingConsentPrompt() {
  const { state, refreshUser } = useAuth();
  const { pathname } = useLocation();
  const user = state.user;
  const [consent, setConsent] = useState<MarketingConsent>({ sms: false, email: false });
  const [dismissedNow, setDismissedNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nudgeOpen, setNudgeOpen] = useState(false);

  useEffect(() => {
    setDismissedNow(false);
    setConsent({ sms: false, email: false });
    setError('');
    setNudgeOpen(false);
  }, [user?.id]);

  if (!state.isAuthenticated || !user || state.isImpersonating || dismissedNow) return null;
  if (HIDDEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;
  if (user.marketing_sms_consent_at || user.marketing_email_consent_at) return null;
  if (user.marketing_consent_answered_at) return null;
  if (user.marketing_consent_deferred_until && Date.parse(user.marketing_consent_deferred_until) > Date.now()) return null;

  const save = async (choice: MarketingConsent | 'defer') => {
    setSaving(true);
    setError('');
    setNudgeOpen(false);
    try {
      await api.put('/api/user/profile', choice === 'defer' ? {
        marketing_consent_defer: true,
      } : {
        marketing_sms_consent: choice.sms,
        marketing_email_consent: choice.email,
      });
      setDismissedNow(true);
      await refreshUser();
    } catch {
      setError('저장하지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const nothingChosen = !consent.sms && !consent.email;

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketing-consent-prompt-title"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 id="marketing-consent-prompt-title" className="text-lg font-extrabold text-forest-900">
            새 기능이 나오면 바로 알려드릴까요?
          </h2>
          <button type="button" onClick={() => { void save('defer'); }} disabled={saving} aria-label="닫기" className="rounded p-1 text-forest-400 hover:bg-forest-50">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-forest-600 mb-4">
          원클릭 모의고사 같은 신기능 출시, 라이브 설명회, 무료 포인트 이벤트 소식을 놓치지 않게 보내드려요.
        </p>
        <MarketingConsentFields value={consent} onChange={setConsent} idPrefix="prompt-consent" showSelectAll={false} showBonus />
        <p className="mt-2 text-right text-xs">
          <Link to="/marketing-consent" className="text-forest-500 underline hover:text-amber-600">광고성 정보 수신 동의 안내 보기</Link>
        </p>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        <button
          type="button"
          onClick={() => { void save({ sms: true, email: true }); }}
          disabled={saving}
          className="mt-5 w-full rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 py-3 text-sm font-bold text-white shadow-md disabled:opacity-50"
        >
          {saving ? '저장 중...' : `모두 동의하고 ${MARKETING_CONSENT_BONUS}포인트 받기`}
        </button>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => { void save(consent); }}
            disabled={saving || nothingChosen}
            className="flex-1 rounded-xl px-3 py-2.5 text-sm font-medium text-forest-600 hover:bg-forest-50 disabled:opacity-40"
          >
            선택한 소식만 받기
          </button>
          <button type="button" onClick={() => setNudgeOpen(true)} disabled={saving}
            className="rounded-xl px-3 py-2.5 text-sm font-medium text-forest-500 hover:bg-forest-50 disabled:opacity-50">
            받지 않기
          </button>
          <button
            type="button"
            onClick={() => { void save('defer'); }}
            disabled={saving}
            className="rounded-xl px-3 py-2.5 text-sm font-medium text-forest-500 hover:bg-forest-50"
          >
            나중에
          </button>
        </div>
        {nudgeOpen && (
          <MarketingBonusNudge
            busy={saving}
            onAccept={() => { void save({ sms: true, email: true }); }}
            onDecline={() => { void save({ sms: false, email: false }); }}
          />
        )}
      </div>
    </div>
  );
}
